import { marketStatuses } from "../constants/market-statuses";

export function getStatus(status: number): string {
  return marketStatuses[status];
}

export function sortHighlightRunners(items: any[]) {
  if (!items) return [];
  const runners = [...items];
  // First, sort by sortPriority
  runners.sort((a, b) => (a?.sortPriority || 0) - (b?.sortPriority || 0));

  // If exactly 3 runners, assume [Home, Away, Draw] and reorder to [Home, Draw, Away]
  if (runners.length === 3) {
    const [r1, r2, r3] = runners;
    return [r1, r3, r2];
  }

  // Find index of "The Draw" for other cases
  const drawIndex = runners.findIndex((item) => item?.runnerName?.includes("The Draw"));

  // If "The Draw" exists and it's not already the second element
  if (drawIndex > -1 && drawIndex !== 1) {
    const [drawItem] = runners.splice(drawIndex, 1); // Remove "The Draw" from its position
    runners.splice(1, 0, drawItem); // Insert "The Draw" at second position
  }

  return runners;
}

export function genericSort<T>(
  array: T[],
  fields: (keyof T)[], // Accept an array of fields for sorting
  order: "asc" | "desc" = "asc",
): T[] {
  return array.sort((a, b) => {
    for (const field of fields) {
      const aValue = a[field];
      const bValue = b[field];

      let comparison = 0;

      // Handle Date objects or date-time strings
      if (aValue instanceof Date && bValue instanceof Date) {
        comparison = aValue.getTime() - bValue.getTime();
      } else if (typeof aValue === "string" && typeof bValue === "string" && Date.parse(aValue) && Date.parse(bValue)) {
        comparison = new Date(aValue).getTime() - new Date(bValue).getTime();
      }

      // Fallback for numbers and strings
      else if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      } else if (typeof aValue === "string" && typeof bValue === "string") {
        comparison = aValue.localeCompare(bValue);
      }

      // If comparison is non-zero, return the result, otherwise move to the next field
      if (comparison !== 0) {
        return order === "desc" ? -comparison : comparison;
      }
    }

    // If all fields are equal, return 0
    return 0;
  });
}

export function inningSort(array: any[]) {
  const regex = /(\d+)[a-z]{2}\sInnings\s(\d+)\sOvers/i;

  // Extract indices and values of "Innings" markets
  const inningsMarkets = array.map((item, index) => ({ item, index })).filter(({ item }) => regex.test(item.marketName));

  // Sort only the "Innings" markets
  const sortedInningsMarkets = inningsMarkets
    .map(({ item }) => item) // Extract the items
    .sort((a, b) => {
      const matchA = a.marketName.match(regex);
      const matchB = b.marketName.match(regex);

      // Safely extract inning and overs values
      const inningA = parseInt(matchA[1], 10); // First number (before "Innings")
      const oversA = parseInt(matchA[2], 10); // Second number (before "Overs")

      const inningB = parseInt(matchB[1], 10); // First number (before "Innings")
      const oversB = parseInt(matchB[2], 10); // Second number (before "Overs")

      // Sort by inning first, then by overs
      if (inningA !== inningB) {
        return inningA - inningB; // Sort by inning
      }
      return oversA - oversB; // Sort by overs if innings are the same
    });

  // Find the index of the first "Innings" market in the original array
  const firstInningIndex = inningsMarkets.length > 0 ? inningsMarkets[0].index : -1;

  // Build the result array
  const result = [...array];

  if (firstInningIndex !== -1) {
    // Remove all the original "Innings" markets
    const filteredResult = result.filter(({ marketName }) => !regex.test(marketName));

    // Insert the sorted "Innings" markets after the first detected "Innings" market
    return [...filteredResult.slice(0, firstInningIndex), ...sortedInningsMarkets, ...filteredResult.slice(firstInningIndex)];
  }

  // If no "Innings" markets are found, return the original array
  return result;
}

export function getSportId(data: any): string {
  const sportId = data?.eventType?.id || data?.SPORT_ID;
  if (!sportId) return "";

  if (data?.isMOddsBookmaker) {
    if (sportId === "4") return "Cricket-BF-BM";
    if (sportId === "1") return "Soccer-BF-BM";
    if (sportId === "2") return "Tennis-BF-BM";
  }

  if (sportId === "4") {
    const isFancy = data?.isFancy || data?.descriptionPrefix === "Fancy";
    const isBookmaker = data?.isBookmaker || data?.descriptionPrefix === "Bookmaker";
    if (isFancy) return "Cricket-Fancy";
    if (isBookmaker) return "Cricket-Bookmaker";
  }

  return sportId;
}
